import type { OptimizelyDatafile } from '#types/p13n'
import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'

type DecisionIdentity = Pick<OptimizelyDecision, 'flagKey' | 'ruleKey' | 'variationKey'>

/**
 * Parses an Optimizely datafile string into the typed project structure.
 * @param datafileString JSON datafile string returned by the Optimizely SDK.
 * @returns Parsed datafile, or `null` when the datafile is missing or invalid.
 */
export const parseOptimizelyDatafile = (datafileString?: string | null): OptimizelyDatafile | null => {
  if (!datafileString) return null

  try {
    return JSON.parse(datafileString) as OptimizelyDatafile
  }
  catch {
    return null
  }
}

/**
 * Returns whether the current Optimizely datafile contains the given feature flag key.
 * @param datafile Current Optimizely project datafile.
 * @param flagKey Optimizely feature flag key to look up.
 * @returns `true` when the flag exists in the datafile.
 */
export const hasFeatureFlagInDatafile = (
  datafile: OptimizelyDatafile | null | undefined,
  flagKey: string
): boolean => {
  if (!datafile) return false

  return datafile.featureFlags.some(({ key }) => key === flagKey)
}

/**
 * Returns whether a decision maps to a currently running Optimizely experiment
 * for the decided flag. Rollouts are intentionally excluded.
 * @param datafile Current Optimizely project datafile.
 * @param decision Decision returned by `user.decide()`.
 * @returns `true` when the decision belongs to a running feature experiment.
 */
export const isDecisionFromRunningExperiment = (
  datafile: OptimizelyDatafile | null | undefined,
  decision?: DecisionIdentity
): boolean => {
  if (!datafile || !decision?.flagKey || !decision.ruleKey || !decision.variationKey) return false

  const featureFlag = datafile.featureFlags.find(({ key }) => key === decision.flagKey)
  if (!featureFlag?.experimentIds?.length) return false

  return featureFlag.experimentIds.some((experimentId) => {
    const experiment = datafile.experiments.find(({ id }) => id === experimentId)

    return experiment?.key === decision.ruleKey
      && experiment.variations.some(({ key }) => key === decision.variationKey)
      && experiment.status?.toLowerCase() === 'running'
  })
}

const extractAttributesFromConditions = (
  conditions: OptimizelyDatafile['typedAudiences'][number]['conditions'] | unknown,
): string[] => {
  if (!Array.isArray(conditions)) return []

  const attrs: string[] = []

  for (const condition of conditions) {
    if (Array.isArray(condition)) {
      attrs.push(...extractAttributesFromConditions(condition))
    }
    else if (condition && typeof condition === 'object' && 'name' in condition && typeof condition.name === 'string' && condition.name) {
      attrs.push(condition.name)
    }
  }

  return attrs
}

/**
 * Collects Optimizely user-attribute names a flag's audiences rely on.
 * Walks the flag's experiments and rollout deliveries in the datafile.
 * @param datafile Current Optimizely project datafile.
 * @param flagKey Optimizely feature flag key to look up.
 * @returns Unique attribute keys from matching `typedAudiences` conditions.
 */
export const getAttributeKeysUsedByFlag = (
  datafile: OptimizelyDatafile | null | undefined,
  flagKey: string
): string[] => {
  if (!datafile || !flagKey) return []

  const featureFlag = datafile.featureFlags?.find(({ key }) => key === flagKey)
  if (!featureFlag) return []

  const audienceIds = new Set<string>()

  for (const experimentId of featureFlag.experimentIds || []) {
    const experiment = datafile.experiments?.find(({ id }) => id === experimentId)
    experiment?.audienceIds?.forEach((id) => audienceIds.add(id))
  }

  const rollout = featureFlag.rolloutId
    ? datafile.rollouts?.find(({ id }) => id === featureFlag.rolloutId)
    : undefined

  rollout?.experiments?.forEach((experiment) => {
    experiment.audienceIds?.forEach((id) => audienceIds.add(id))
  })

  if (!audienceIds.size) return []

  const keys = new Set<string>()

  for (const audience of datafile.typedAudiences || []) {
    if (!audienceIds.has(audience.id)) continue
    extractAttributesFromConditions(audience.conditions).forEach((name) => keys.add(name))
  }

  return [...keys]
}
